import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ShareIcon,
  EyeIcon,
  PencilIcon,
  CalendarIcon,
  UserIcon,
  LinkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

import { shareService } from '../../services/sharing';

const ShareModal = ({ isOpen, onClose, entryId, entryTitle, onShareCreated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shareResult, setShareResult] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset
  } = useForm({
    defaultValues: {
      permission: 'view',
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from now
      sharedWithEmail: ''
    }
  });

  const permission = watch('permission');

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const shareData = {
        ...data,
        expiresAt: new Date(data.expiresAt + 'T23:59:59').toISOString(),
        sharedWithEmail: data.sharedWithEmail || undefined
      };

      const response = await shareService.createShareToken(entryId, shareData);
      setShareResult(response.data);
      toast.success('Share link created successfully!');
      
      if (onShareCreated) {
        onShareCreated(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create share');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyShareLink = () => {
    if (shareResult?.shareUrl) {
      navigator.clipboard.writeText(shareResult.shareUrl);
      toast.success('Share link copied to clipboard!');
    }
  };

  const handleClose = () => {
    setShareResult(null);
    reset();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {!shareResult ? (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <ShareIcon className="h-6 w-6 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Share Entry</h3>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                {/* Entry Info */}
                <div className="p-6 border-b border-gray-200 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-1">{entryTitle}</h4>
                  <p className="text-sm text-gray-600">
                    You're about to share this diary entry
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                  {/* Permission Level */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Permission Level
                    </label>
                    <div className="space-y-3">
                      <motion.label
                        className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          permission === 'view'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <input
                          {...register('permission')}
                          type="radio"
                          value="view"
                          className="sr-only"
                        />
                        <EyeIcon className="h-5 w-5 text-blue-600 mr-3" />
                        <div>
                          <div className="font-medium text-gray-900">View Only</div>
                          <div className="text-sm text-gray-500">Can read but not edit</div>
                        </div>
                      </motion.label>

                      <motion.label
                        className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          permission === 'edit'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <input
                          {...register('permission')}
                          type="radio"
                          value="edit"
                          className="sr-only"
                        />
                        <PencilIcon className="h-5 w-5 text-green-600 mr-3" />
                        <div>
                          <div className="font-medium text-gray-900">Can Edit</div>
                          <div className="text-sm text-gray-500">Can read and modify</div>
                        </div>
                      </motion.label>
                    </div>
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <CalendarIcon className="h-4 w-4 inline mr-1" />
                      Expires On
                    </label>
                    <input
                      {...register('expiresAt', {
                        required: 'Expiry date is required',
                        validate: value => {
                          const selectedDate = new Date(value);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return selectedDate >= today || 'Expiry date must be in the future';
                        }
                      })}
                      type="date"
                      className={`input-field ${errors.expiresAt ? 'border-red-500' : ''}`}
                    />
                    {errors.expiresAt && (
                      <p className="mt-1 text-sm text-red-600">{errors.expiresAt.message}</p>
                    )}
                  </div>

                  {/* Email (Optional) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <UserIcon className="h-4 w-4 inline mr-1" />
                      Share with Email (Optional)
                    </label>
                    <input
                      {...register('sharedWithEmail', {
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      type="email"
                      placeholder="Enter email address"
                      className={`input-field ${errors.sharedWithEmail ? 'border-red-500' : ''}`}
                    />
                    {errors.sharedWithEmail && (
                      <p className="mt-1 text-sm text-red-600">{errors.sharedWithEmail.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      If provided, we'll send a notification email
                    </p>
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary flex items-center justify-center space-x-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Creating Share...</span>
                      </>
                    ) : (
                      <>
                        <ShareIcon className="h-4 w-4" />
                        <span>Create Share Link</span>
                      </>
                    )}
                  </motion.button>
                </form>
              </>
            ) : (
              <>
                {/* Success Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <ShareIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Share Created!</h3>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                {/* Share Details */}
                <div className="p-6 space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium mb-2">
                      Your share link has been created successfully!
                    </p>
                    <p className="text-green-700 text-sm">
                      {shareResult.sharedWithEmail 
                        ? `A notification has been sent to ${shareResult.sharedWithEmail}`
                        : 'You can now share this link with anyone you want'
                      }
                    </p>
                  </div>

                  {/* Share URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Share Link
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        value={shareResult.shareUrl}
                        readOnly
                        className="flex-1 input-field rounded-r-none bg-gray-50"
                      />
                      <motion.button
                        onClick={copyShareLink}
                        className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <LinkIcon className="h-4 w-4" />
                        <span>Copy</span>
                      </motion.button>
                    </div>
                  </div>

                  {/* Share Info */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <EyeIcon className="h-4 w-4" />
                      <span>{shareResult.permission === 'view' ? 'View only' : 'Can edit'}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <ClockIcon className="h-4 w-4" />
                      <span>Expires {new Date(shareResult.expiresAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <motion.button
                    onClick={handleClose}
                    className="w-full btn-secondary"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Done
                  </motion.button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareModal;
